import {
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  CheckCircle as CheckCircleIcon,
  ContactPhone as ContactPhoneIcon,
  Error as ErrorIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  Groups as GroupsIcon,
  Map as MapIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme
} from '@mui/material';
import React, { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
// import FiltersBar from '../components/FiltersBar.jsx'; // Desabilitado para validação com dados mockados

// Componente de KPI Card moderno
const KpiCard = ({ title, value, subtitle, icon, trend, trendValue, color = 'primary', loading = false }) => {
  const theme = useTheme();
  const isPositive = trend === 'up';
  
  const colorMap = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${colorMap[color]}15 0%, ${colorMap[color]}05 100%)`,
        border: `1px solid ${colorMap[color]}30`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${colorMap[color]}25`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${colorMap[color]} 0%, ${colorMap[color]}CC 100%)`,
              boxShadow: `0 4px 14px ${colorMap[color]}40`,
            }}
          >
            {React.cloneElement(icon, { sx: { color: 'inherit', fontSize: 28 } })}
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={isPositive ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
              label={`${isPositive ? '+' : ''}${trendValue}%`}
              sx={{
                bgcolor: isPositive ? 'success.light' : 'error.light',
                color: isPositive ? 'success.dark' : 'error.dark',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>
        <Typography variant="h3" sx={{ fontWeight: 700, color: colorMap[color], mb: 0.5 }}>
          {loading ? <CircularProgress size={32} /> : value}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Componente de Status Badge
const StatusBadge = ({ status }) => {
  const statusConfig = {
    realizado: { color: 'success', label: 'Realizado', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    pendente: { color: 'warning', label: 'Pendente', icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
    atrasado: { color: 'error', label: 'Atrasado', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    agendado: { color: 'info', label: 'Agendado', icon: <EventAvailableIcon sx={{ fontSize: 14 }} /> },
  };

  const config = statusConfig[status] || statusConfig.pendente;

  return (
    <Chip
      size="small"
      icon={config.icon}
      label={config.label}
      color={config.color}
      sx={{ fontWeight: 600 }}
    />
  );
};

// Dados mockados para demonstração
const mockKpis = {
  totalInspecoes: 1247,
  realizadas: 1089,
  pendentes: 98,
  atrasadas: 60,
  taxaContato: 87.3,
  taxaRealizacao: 91.2,
};

const mockChartData = [
  { name: 'Seg', previsto: 45, realizado: 42, taxa: 93 },
  { name: 'Ter', previsto: 52, realizado: 48, taxa: 92 },
  { name: 'Qua', previsto: 48, realizado: 45, taxa: 94 },
  { name: 'Qui', previsto: 55, realizado: 50, taxa: 91 },
  { name: 'Sex', previsto: 50, realizado: 47, taxa: 94 },
  { name: 'Sáb', previsto: 30, realizado: 28, taxa: 93 },
  { name: 'Dom', previsto: 20, realizado: 18, taxa: 90 },
];

const mockEquipesData = [
  { equipe: 'PA-MARAB-LM-100', previsto: 45, realizado: 42, taxa: 93.3, contato: 95.2, status: 'realizado' },
  { equipe: 'PA-MARAB-LM-101', previsto: 52, realizado: 48, taxa: 92.3, contato: 88.5, status: 'realizado' },
  { equipe: 'PA-MARAB-LM-102', previsto: 48, realizado: 35, taxa: 72.9, contato: 78.3, status: 'pendente' },
  { equipe: 'PA-MARAB-LM-103', previsto: 55, realizado: 30, taxa: 54.5, contato: 65.0, status: 'atrasado' },
  { equipe: 'PA-MARAB-LM-104', previsto: 50, realizado: 47, taxa: 94.0, contato: 92.1, status: 'realizado' },
  { equipe: 'PA-MARAB-LM-105', previsto: 42, realizado: 40, taxa: 95.2, contato: 97.8, status: 'realizado' },
];

const mockStatusData = [
  { name: 'Realizadas', value: 1089, color: '#4caf50' },
  { name: 'Pendentes', value: 98, color: '#ff9800' },
  { name: 'Atrasadas', value: 60, color: '#f44336' },
];

const mockTendenciaData = [
  { data: '01/12', taxa: 85 },
  { data: '02/12', taxa: 87 },
  { data: '03/12', taxa: 86 },
  { data: '04/12', taxa: 89 },
  { data: '05/12', taxa: 91 },
  { data: '06/12', taxa: 88 },
  { data: '07/12', taxa: 92 },
];

const InspecoesSeguranca = () => {
  const [activeTab, setActiveTab] = useState(0);


  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)',
            }}
          >
            <SecurityIcon sx={{ color: 'inherit', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Inspeções de Segurança
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie e monitore as inspeções de segurança das equipes em tempo real
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Conteúdo principal - Dados Mockados para Validação */}
      <Alert 
        severity="success" 
        sx={{ mb: 3, borderRadius: 2 }}
        icon={<SecurityIcon />}
      >
        <strong>Modo de Validação:</strong> Exibindo dados mockados para validação do layout. Integração com API será feita posteriormente.
      </Alert>

      {(
        <>
          {/* KPIs principais */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Total de Inspeções"
                value={mockKpis.totalInspecoes.toLocaleString()}
                subtitle="No período selecionado"
                icon={<AssignmentIcon />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Taxa de Realização"
                value={`${mockKpis.taxaRealizacao}%`}
                subtitle={`${mockKpis.realizadas} inspeções realizadas`}
                icon={<CheckCircleIcon />}
                trend="up"
                trendValue={3.2}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Taxa de Contato"
                value={`${mockKpis.taxaContato}%`}
                subtitle="Contato com cliente"
                icon={<ContactPhoneIcon />}
                trend="up"
                trendValue={1.8}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Inspeções Atrasadas"
                value={mockKpis.atrasadas}
                subtitle={`${mockKpis.pendentes} pendentes`}
                icon={<WarningIcon />}
                trend="down"
                trendValue={5.1}
                color="error"
              />
            </Grid>
          </Grid>

          {/* Tabs de navegação */}
          <Paper sx={{ mb: 3, borderRadius: 2 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<BarChartIcon />} label="Visão Geral" iconPosition="start" />
              <Tab icon={<GroupsIcon />} label="Por Equipe" iconPosition="start" />
              <Tab icon={<MapIcon />} label="Mapa" iconPosition="start" />
              <Tab icon={<TimelineIcon />} label="Tendências" iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Tab 0: Visão Geral */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Gráfico Previsto x Realizado */}
              <Grid item xs={12} lg={8}>
                <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Inspeções Previstas vs Realizadas
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Comparativo diário da semana
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Chip 
                        size="small" 
                        color="info" 
                        label="Previsto" 
                      />
                      <Chip 
                        size="small" 
                        color="success" 
                        label="Realizado" 
                      />
                    </Box>
                  </Box>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={mockChartData} barGap={8}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          border: '1px solid #e0e0e0',
                          borderRadius: 8,
                        }}
                      />
                      <Bar dataKey="previsto" fill="#1976d2" radius={[4, 4, 0, 0]} name="Previsto" />
                      <Bar dataKey="realizado" fill="#4caf50" radius={[4, 4, 0, 0]} name="Realizado" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Gráfico de Pizza - Status */}
              <Grid item xs={12} lg={4}>
                <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Status das Inspeções
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Distribuição por status
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={mockStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {mockStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    {mockStatusData.map((item) => (
                      <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                        <Typography variant="caption">{item.name}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* Taxa de Contato por Período */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Evolução da Taxa de Realização
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Últimos 7 dias
                      </Typography>
                    </Box>
                    <Chip
                      icon={<TrendingUpIcon />}
                      label="Meta: 90%"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={mockTendenciaData}>
                      <defs>
                        <linearGradient id="colorTaxa" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1976d2" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1976d2" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="data" tick={{ fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        formatter={(value) => [`${value}%`, 'Taxa de Realização']}
                        contentStyle={{
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          border: '1px solid #e0e0e0',
                          borderRadius: 8,
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="taxa" 
                        stroke="#1976d2" 
                        fillOpacity={1} 
                        fill="url(#colorTaxa)" 
                        strokeWidth={3}
                      />
                      {/* Linha de meta */}
                      <Line 
                        type="monotone" 
                        dataKey={() => 90} 
                        stroke="#4caf50" 
                        strokeDasharray="5 5" 
                        strokeWidth={2}
                        dot={false}
                        name="Meta"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Por Equipe */}
          {activeTab === 1 && (
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Desempenho por Equipe
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ranking de equipes por taxa de realização
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Ranking</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Equipe</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Previsto</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Realizado</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Taxa Realização</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Taxa Contato</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockEquipesData
                      .sort((a, b) => b.taxa - a.taxa)
                      .map((row, index) => (
                      <TableRow 
                        key={row.equipe}
                        sx={{ 
                          '&:hover': { bgcolor: 'action.hover' },
                          bgcolor: index % 2 === 0 ? 'background.paper' : 'grey.50'
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}º`}
                            size="small"
                            sx={{ 
                              fontWeight: 700,
                              bgcolor: index < 3 ? 'success.light' : 'grey.200',
                              color: index < 3 ? 'success.dark' : 'text.primary'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.equipe}</TableCell>
                        <TableCell align="center">{row.previsto}</TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {row.realizado}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={row.taxa}
                              sx={{
                                width: 80,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: row.taxa >= 90 ? 'success.main' : row.taxa >= 70 ? 'warning.main' : 'error.main',
                                  borderRadius: 4,
                                }
                              }}
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600, 
                                minWidth: 50,
                                color: row.taxa >= 90 ? 'success.main' : row.taxa >= 70 ? 'warning.main' : 'error.main'
                              }}
                            >
                              {row.taxa.toFixed(1)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<PhoneIcon sx={{ fontSize: 14 }} />}
                            label={`${row.contato}%`}
                            size="small"
                            color={row.contato >= 90 ? 'success' : row.contato >= 75 ? 'warning' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <StatusBadge status={row.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tab 2: Mapa */}
          {activeTab === 2 && (
            <Paper sx={{ p: 3, borderRadius: 3, minHeight: 500 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Mapa de Inspeções
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Localização das inspeções em tempo real
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip icon={<CheckCircleIcon />} label="Realizadas" color="success" size="small" />
                  <Chip icon={<ScheduleIcon />} label="Pendentes" color="warning" size="small" />
                  <Chip icon={<ErrorIcon />} label="Atrasadas" color="error" size="small" />
                </Box>
              </Box>
              
              {/* Placeholder do Mapa */}
              <Box
                sx={{
                  height: 450,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <MapIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Mapa de Inspeções
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Integração com Google Maps ou Leaflet será implementada aqui
                </Typography>
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>42</Typography>
                    </Avatar>
                    <Typography variant="caption">Realizadas</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>15</Typography>
                    </Avatar>
                    <Typography variant="caption">Pendentes</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'error.main', mx: 'auto', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>8</Typography>
                    </Avatar>
                    <Typography variant="caption">Atrasadas</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Tab 3: Tendências */}
          {activeTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Análise de Tendências
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={mockChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          border: '1px solid #e0e0e0',
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="previsto" 
                        stroke="#1976d2" 
                        strokeWidth={3}
                        dot={{ fill: '#1976d2', strokeWidth: 2, r: 5 }}
                        name="Previsto"
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="realizado" 
                        stroke="#4caf50" 
                        strokeWidth={3}
                        dot={{ fill: '#4caf50', strokeWidth: 2, r: 5 }}
                        name="Realizado"
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="taxa" 
                        stroke="#ff9800" 
                        strokeWidth={3}
                        strokeDasharray="5 5"
                        dot={{ fill: '#ff9800', strokeWidth: 2, r: 5 }}
                        name="Taxa (%)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Cards de métricas adicionais */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <SpeedIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Média Semanal
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    92.4%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taxa média de realização
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <EventAvailableIcon color="success" sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Melhor Dia
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                    Sexta
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    94% de realização
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <EventBusyIcon color="error" sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Pior Dia
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'error.main' }}>
                    Domingo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    90% de realização
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Container>
  );
};

export default InspecoesSeguranca;

